import { Close, Send } from "@mui/icons-material";
import { Button, Drawer, IconButton, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import InputField from "components/Input/InputField";
import { API_URL } from "const/constants";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

const INITIAL_FORM = {
  name: "",
  email: "",
  phone: "",
};

const emailOk = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

/**
 * Admin invites an external referral partner. Sends name, email and phone; the backend emails
 * an onboarding / invitation link. Further onboarding steps are built separately.
 */
const InviteReferralDrawer = ({ open, onClose, onInvited }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm(INITIAL_FORM);
    setErrors({});
  }, [open]);

  const { mutate: sendInvite, isLoading } = useMutation(
    async (payload) => {
      const res = await axios.post(`${API_URL}/referral-partners/invitations`, payload);
      return res?.data;
    },
    {
      onSuccess: (data) => {
        toast.success("Invitation sent — they’ll receive an onboarding link to join");
        onInvited?.();
        onClose?.();
      },
      onError: (err) => {
        toast.error(
          err?.response?.data?.errorMessage ||
            err?.response?.data?.message ||
            "Could not send invitation"
        );
      },
    }
  );

  const patch = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Name is required";
    if (!form.email.trim()) next.email = "Email is required";
    else if (!emailOk(form.email)) next.email = "Enter a valid email";
    if (!form.phone.trim()) next.phone = "Phone is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;
    sendInvite({
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim(),
    });
  };

  return (
    <Drawer anchor="right" open={open} onClose={isLoading ? undefined : onClose}>
      <div className="w-full sm:w-[420px] max-w-[100vw] h-full flex flex-col bg-app-surface text-app-text">
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-app-border">
          <div>
            <Typography variant="h6" className="font-semibold">
              Invite referral
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              We’ll email them a link to join and complete onboarding (e.g. /referral/onboarding/…
              ).
            </Typography>
          </div>
          <IconButton onClick={onClose} disabled={isLoading} aria-label="Close">
            <Close />
          </IconButton>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            <InputField
              label="Full name"
              name="name"
              required
              placeholder="e.g. Jane Smith"
              value={form.name}
              onChange={patch}
              error={Boolean(errors.name)}
              helperText={errors.name}
              autoFocus
            />
            <InputField
              label="Email"
              name="email"
              type="email"
              required
              placeholder="partner@example.com"
              value={form.email}
              onChange={patch}
              error={Boolean(errors.email)}
              helperText={errors.email}
            />
            <InputField
              label="Phone"
              name="phone"
              type="tel"
              required
              placeholder="+61 …"
              value={form.phone}
              onChange={patch}
              error={Boolean(errors.phone)}
              helperText={errors.phone}
            />
          </div>

          <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-app-border">
            <Button variant="outlined" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" startIcon={<Send />} disabled={isLoading}>
              {isLoading ? "Sending…" : "Send invitation"}
            </Button>
          </div>
        </form>
      </div>
    </Drawer>
  );
};

export default InviteReferralDrawer;
